import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, ThemedCard, ThemedButton } from './ThemeContext';

const LocalGamesManager = ({ user }) => {
  const { woodStyles } = useTheme();
  const navigate = useNavigate();
  const [localGames, setLocalGames] = useState([]);

  // Load all local games from localStorage
  useEffect(() => {
    const loadLocalGames = () => {
      const games = [];

      // Find all local game keys in localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('chess_game_local_')) {
          try {
            const gameData = JSON.parse(localStorage.getItem(key));
            const gameId = key.replace('chess_game_', '');

            // Only show games created by this user
            if (gameData && gameData.createdBy === user.id) {
              games.push({
                id: gameId,
                createdAt: new Date(gameData.createdAt || Date.now()),
                status: gameData.status || 'active',
                playerColor: gameData.playerColor || 'white',
                isAnarchy: gameData.isAnarchy || false,
                moves: gameData.moves?.length || 0,
                botSlotConfig: gameData.botSlotConfig || {}
              });
            }
          } catch (error) {
            console.error(`Error parsing game data for ${key}:`, error);
          }
        }
      }

      // Sort by most recent first
      games.sort((a, b) => b.createdAt - a.createdAt);
      setLocalGames(games);
    };

    loadLocalGames();

    // Add storage event listener to detect changes
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('chess_game_local_')) {
        loadLocalGames();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user.id]);

  // Check if any active game exists
  const hasActiveGame = localGames.some(game => game.status === 'active');

  // Delete a local game
  const handleDeleteGame = (gameId) => {
    if (window.confirm('Are you sure you want to delete this game?')) {
      localStorage.removeItem(`chess_game_${gameId}`);
      setLocalGames(games => games.filter(game => game.id !== gameId));

      // Dispatch a custom event to notify other components about the game deletion
      const gameDeletedEvent = new CustomEvent('bot-game-deleted', {
        detail: { gameId }
      });
      window.dispatchEvent(gameDeletedEvent);
    }
  };

  // Continue a local game
  const handleContinueGame = (gameId) => {
    navigate(`/game_local/${gameId}`);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  // Get slot configuration description
  const getSlotConfigDescription = (config) => {
    if (!config) return 'Random moves';

    const slots = [];

    if (config.slot1) slots.push(config.slot1 === 'random' ? 'Random' : `SF${config.slot1}`);
    if (config.slot2) slots.push(config.slot2 === 'random' ? 'Random' : `SF${config.slot2}`);
    if (config.slot3) slots.push(config.slot3 === 'random' ? 'Random' : `SF${config.slot3}`);

    return slots.join(' / ');
  };

  if (localGames.length === 0) {
    return null; // Don't show the component if there are no local games
  }

  return (
    <ThemedCard className="mb-8">
      <div className={`px-3 py-2 border-b ${woodStyles.card.border}`}>
        <span className={`text-sm font-semibold ${woodStyles.text.primary}`}>
          Your Bot Games
        </span>
      </div>

      <div className="p-4">
        <div className="space-y-3">
          {localGames.map((game) => (
            <div key={game.id} className={`p-3 rounded-lg ${woodStyles.surface.secondary}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className={`font-medium ${woodStyles.text.primary}`}>
                    {game.status === 'active' ? '🎮 ' : '🏁 '}
                    Bot Game ({formatDate(game.createdAt)})
                  </div>
                  <div className={`text-sm ${woodStyles.text.secondary}`}>
                    {game.moves} moves • {game.playerColor} • {game.isAnarchy ? 'Anarchy' : 'Normal'}
                  </div>
                  <div className={`text-xs mt-1 ${woodStyles.text.secondary}`}>
                    Bot: {getSlotConfigDescription(game.botSlotConfig)}
                  </div>
                </div>
                <div className="flex gap-2">
                  {game.status === 'active' && (
                    <ThemedButton
                      onClick={() => handleContinueGame(game.id)}
                      variant="primary"
                    >
                      Continue
                    </ThemedButton>
                  )}
                  <ThemedButton
                    onClick={() => handleDeleteGame(game.id)}
                    variant="secondary"
                  >
                    Delete
                  </ThemedButton>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasActiveGame && (
          <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg text-sm">
            <p className={woodStyles.text.primary}>
              ⚠️ You have active bot games. Finish or delete them before creating new ones.
            </p>
          </div>
        )}
      </div>
    </ThemedCard>
  );
};

export default LocalGamesManager;